import { Button, ButtonGroup, DropdownMenu } from '@components'
import { IAccountBalance } from '@query'
import { formatCurrency } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaCaretDown } from 'react-icons/fa'
import styled from 'styled-components'
import { OptionIcon } from './CashAccounts'

interface Props {
  accounts: IAccountBalance[]
  selected: IAccountBalance | null
  accountKey: (account: IAccountBalance) => string
  onSelect: (key: string | null) => void
}

export const CashAccountMenu: React.FC<Props> = ({ accounts, selected, accountKey, onSelect }) => {
  const [t] = useTranslation()
  if (accounts.length <= 1) return null

  return (
    <DropdownMenu
      placement="bottom-end"
      items={[
        <ButtonGroup key="accounts">
          <AccountMenuButton
            key="all"
            type="button"
            isSecondary
            size="sm"
            icon={<OptionIcon $src={null} />}
            onClick={() => onSelect(null)}
          >
            <OptionName>{t('dashboard.cash.allAccounts')}</OptionName>
            <OptionAmount>
              {formatCurrency(accounts.reduce((sum, account) => sum + account.balance.amount, 0))}
            </OptionAmount>
          </AccountMenuButton>
          {accounts.map(account => (
            <AccountMenuButton
              key={accountKey(account)}
              type="button"
              isSecondary
              size="sm"
              icon={<OptionIcon $src={account.icon} />}
              onClick={() => onSelect(accountKey(account))}
            >
              <OptionName>{account.name}</OptionName>
              <OptionAmount>{formatCurrency(account.balance.amount)}</OptionAmount>
            </AccountMenuButton>
          ))}
        </ButtonGroup>
      ]}
    >
      <TriggerButton type="button" size="sm" isSecondary iconRight={<FaCaretDown />}>
        {selected ? selected.name : t('dashboard.cash.accountCount', { count: accounts.length })}
      </TriggerButton>
    </DropdownMenu>
  )
}

const TriggerButton = styled(Button)`
  &&.is-secondary {
    color: ${({ theme }) => theme.dashboard.onNavyLabel};
    background: transparent;

    &:hover,
    &:focus,
    &:active {
      background: transparent;
      color: ${({ theme }) => theme.dashboard.onNavyLabel};
      filter: none;
    }

    svg {
      color: ${({ theme }) => theme.dashboard.onNavyLabel};
    }
  }
`

const AccountMenuButton = styled(Button)`
  width: 18rem;
  justify-content: flex-start;
  text-align: left;

  /* Button wraps children in a single span; make that the name | amount row. */
  > span {
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.sm}rem;
  }
`

const OptionName = styled.span`
  flex: 1 1 8rem;
  min-width: 8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.menuBg};
`

const OptionAmount = styled.span`
  flex: 0 0 auto;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.dashboard.textSubtle};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`
